import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Space,
  Card,
  Row,
  Col,
  Popconfirm,
  Tag,
  Spin,
  Empty,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import {
  getTransactions,
  createTransaction,
  updateTransaction,
  deleteTransaction,
  getCategories,
  type Transaction,
} from '../api/budget';

interface TransactionFormData {
  date: Dayjs;
  amount: number;
  type: 'spend' | 'income' | 'savings' | 'investment';
  category: string;
  note?: string;
  recurrence?: string;
}

const Transactions: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [form] = Form.useForm<TransactionFormData>();

  // Filter states
  const [typeFilter, setTypeFilter] = useState<'spend' | 'income' | 'savings' | 'investment' | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [categorySearch, setCategorySearch] = useState<string | undefined>();

  const recurrenceOptions = [
    { label: 'None', value: 'none' },
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Bi-weekly', value: 'biweekly' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Quarterly', value: 'quarterly' },
    { label: 'Yearly', value: 'yearly' },
  ];

  // Fetch categories and transactions on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const [catResp, txResp] = await Promise.all([
          getCategories(),
          getTransactions(),
        ]);
        if (catResp.ok && catResp.data) {
          const cats = catResp.data;
          const allCats = [
            ...(cats.spend ?? []), ...(cats.investment ?? []),
          ].map((name, i) => ({ id: String(i), name, type: 'spend' as const }));
          setCategories(allCats);
        }
        if (txResp.ok && txResp.data) setTransactions(txResp.data);
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
        message.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (typeFilter) params.type = typeFilter;
      if (dateRange) {
        params.from = dateRange[0].format('YYYY-MM-DD');
        params.to = dateRange[1].format('YYYY-MM-DD');
      }
      if (categorySearch) params.category = categorySearch;

      const resp = await getTransactions(params);
      if (resp.ok && resp.data) setTransactions(resp.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      message.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [typeFilter, dateRange, categorySearch]);

  const handleAddTransaction = () => {
    setEditingTransaction(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditTransaction = (transaction: Transaction) => {
    setEditingTransaction(transaction);
    form.setFieldsValue({
      date: dayjs(transaction.date),
      amount: transaction.amount,
      type: transaction.type,
      category: transaction.category,
      note: transaction.note,
      recurrence: transaction.recurrence || 'none',
    });
    setModalVisible(true);
  };

  const handleDeleteTransaction = async (id: string) => {
    try {
      await deleteTransaction(id);
      message.success('Transaction deleted');
      fetchTransactions();
    } catch (error) {
      console.error('Failed to delete transaction:', error);
      message.error('Failed to delete transaction');
    }
  };

  const handleModalOk = async () => {
    try {
      const formData = await form.validateFields();

      const payload = {
        date: formData.date.format('YYYY-MM-DD'),
        amount: formData.amount,
        type: formData.type,
        category: formData.category,
        note: formData.note,
        recurrence: formData.recurrence === 'none' ? undefined : formData.recurrence,
      };

      if (editingTransaction) {
        await updateTransaction(editingTransaction.id, payload);
        message.success('Transaction updated');
      } else {
        await createTransaction(payload);
        message.success('Transaction created');
      }

      setModalVisible(false);
      form.resetFields();
      fetchTransactions();
    } catch (error) {
      console.error('Failed to save transaction:', error);
      message.error('Failed to save transaction');
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingTransaction(null);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getTypeColor = (type: string): string => {
    if (type === 'spend') return '#f5222d';
    if (type === 'income') return '#52c41a';
    if (type === 'savings') return '#1890ff';
    return '#722ed1';
  };

  const getTypeLabel = (type: string): string => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: '12%',
      sorter: (a: Transaction, b: Transaction) =>
        new Date(a.date).getTime() - new Date(b.date).getTime(),
      render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: '15%',
      filters: categories.map((cat) => ({
        text: cat.name,
        value: cat.name,
      })),
      onFilter: (value: boolean | React.Key, record: Transaction) => record.category === value,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: '15%',
      sorter: (a: Transaction, b: Transaction) => a.amount - b.amount,
      render: (amount: number, record: Transaction) => (
        <span style={{ color: getTypeColor(record.type), fontWeight: 'bold' }}>
          {record.type === 'spend' ? '-' : '+'}
          {formatCurrency(amount)}
        </span>
      ),
      align: 'right' as const,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '12%',
      filters: [
        { text: 'Spend', value: 'spend' },
        { text: 'Income', value: 'income' },
        { text: 'Savings', value: 'savings' },
        { text: 'Investment', value: 'investment' },
      ],
      onFilter: (value: boolean | React.Key, record: Transaction) => record.type === value,
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>{getTypeLabel(type)}</Tag>
      ),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      width: '25%',
      ellipsis: true,
      render: (note?: string) => note || '-',
    },
    {
      title: 'Recurrence',
      dataIndex: 'recurrence',
      key: 'recurrence',
      width: '12%',
      render: (recurrence?: string) => {
        if (!recurrence || recurrence === 'none') return '-';
        return (
          <Tag color="blue">
            {recurrenceOptions.find((opt) => opt.value === recurrence)?.label || recurrence}
          </Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '9%',
      render: (_: any, record: Transaction) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditTransaction(record)}
          />
          <Popconfirm
            title="Delete Transaction"
            description="Are you sure you want to delete this transaction?"
            onConfirm={() => handleDeleteTransaction(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Card style={{ marginBottom: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ margin: 0 }}>Transactions</h1>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddTransaction}>
              Add Transaction
            </Button>
          </div>

          {/* Filters */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Select
                placeholder="Filter by Type"
                allowClear
                style={{ width: '100%' }}
                value={typeFilter}
                onChange={setTypeFilter}
                options={[
                  { label: 'All Types', value: undefined },
                  { label: 'Spend', value: 'spend' },
                  { label: 'Income', value: 'income' },
                  { label: 'Savings', value: 'savings' },
                  { label: 'Investment', value: 'investment' },
                ]}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                placeholder={['Start Date', 'End Date']}
                value={dateRange}
                onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
                format="MMM DD, YYYY"
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Select
                placeholder="Search by Category"
                allowClear
                style={{ width: '100%' }}
                value={categorySearch}
                onChange={setCategorySearch}
                options={categories.map((cat) => ({
                  label: cat.name,
                  value: cat.name,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Button
                style={{ width: '100%' }}
                onClick={() => {
                  setTypeFilter(undefined);
                  setDateRange(null);
                  setCategorySearch(undefined);
                }}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* Transactions Table */}
      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={transactions.map((t) => ({ ...t, key: t.id }))}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} transactions`,
            }}
            locale={{ emptyText: <Empty description="No transactions found" /> }}
            scroll={{ x: 1200 }}
          />
        </Spin>
      </Card>

      {/* Add/Edit Transaction Modal */}
      <Modal
        title={editingTransaction ? 'Edit Transaction' : 'Add Transaction'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText={editingTransaction ? 'Update' : 'Create'}
        width={600}
      >
        <Form<TransactionFormData>
          form={form}
          layout="vertical"
          initialValues={{
            recurrence: 'none',
            type: 'spend',
          }}
        >
          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: 'Please select a date' }]}
          >
            <DatePicker style={{ width: '100%' }} format="MMM DD, YYYY" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true, message: 'Please select a type' }]}
          >
            <Select
              options={[
                { label: 'Spend', value: 'spend' },
                { label: 'Income', value: 'income' },
                { label: 'Savings', value: 'savings' },
                { label: 'Investment', value: 'investment' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select
              placeholder="Select a category"
              options={categories.map((cat) => ({
                label: cat.name,
                value: cat.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Amount"
            rules={[
              { required: true, message: 'Please enter an amount' },
              {
                pattern: /^[0-9]+(\.[0-9]{1,2})?$/,
                message: 'Please enter a valid amount',
              },
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              placeholder="0.00"
              prefix="$"
              step={0.01}
              min={0}
            />
          </Form.Item>

          <Form.Item name="recurrence" label="Recurrence">
            <Select
              placeholder="Select recurrence"
              options={recurrenceOptions}
            />
          </Form.Item>

          <Form.Item name="note" label="Note (Optional)">
            <Input placeholder="Add a note..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Transactions;
